/*
usage:
  const callback = (element, mouseX, mouseY) => {
    const tagInfo = prepareTagInfo(element, mouseX, mouseY);

    const coordinates = generateCoordinates(
      element,
      tagInfo.data.bugOffsetX,
      tagInfo.data.bugOffsetY
    );

    console.log({tagInfo, coordinates});
  };

  selectElement(callback);
*/

type ElementMeasurements = {
  width: number;
  height: number;
  left: number;
  top: number;
};

type ElementAfterEvent = {
  mouseX: number;
  mouseY: number;
  faderElement: HTMLElement;
  outlineElement: HTMLElement;
  previousValue: ElementMeasurements;
};

const updateOutlineElement = (
  element: HTMLElement,
  attributes: ElementMeasurements
) => {
  const { width, height } = attributes;

  if (width === 0 && height === 0) {
    element.style.display = 'none';
  } else {
    element.style.display = 'block';
  }

  Object.keys(attributes).forEach((attribute: string) => {
    element.style[attribute] = `${attributes[attribute]}px`;
  });
};

const createInstructionElement = (): HTMLElement => {
  const isTouch =
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    navigator.msMaxTouchPoints > 0;

  const instructionElement = document.createElement('div');

  instructionElement.id = 'bugherd_instruction';

  instructionElement.style.position = 'fixed';
  instructionElement.style.bottom = '20px';
  instructionElement.style.background = 'transparent';
  instructionElement.style.zIndex = '2147483647';
  instructionElement.style.pointerEvents = 'none';
  instructionElement.style.width = '100%';
  instructionElement.style.animation = 'bugherdInstruction 4s ease 1';
  instructionElement.style.transform = 'translateY(150%)';

  instructionElement.innerHTML = `<div style="box-shadow: 0 10px 10px 0 rgb(0 57 128 / 20%); text-align: center; max-width: 500px; width: 80%; border-radius: 5px; box-sizing: border-box; margin: 0 auto; background: #3f71e0; color: #fff; padding: 20px;">
<style>
  @keyframes bugherdInstruction {
    0% { transform: translateY(150%); }
    10% { transform: translateY(0%); }
    90% { transform: translateY(0%); }
    100% { transform: translateY(150%); }
  }
</style>
  <strong>${isTouch ? 'Press' : 'Click'} an element</strong> to give feedback
</div>`;

  return instructionElement;
};

const createOutlineElement = (): HTMLElement => {
  const outlineElement = document.createElement('div');

  outlineElement.id = 'bugherd_selector';

  outlineElement.style.position = 'fixed';
  outlineElement.style.outline = '3px solid #5ab6e0';
  outlineElement.style.cursor = 'crosshair';
  outlineElement.style.zIndex = '2147483647';
  outlineElement.style.pointerEvents = 'none';

  return outlineElement;
};

const getElementAtPosition = (x: number, y: number): Element => {
  return document.elementFromPoint(x, y);
};

const createFaderElement = (): HTMLElement => {
  const fader = document.createElement('div');
  fader.style.position = 'fixed';
  fader.style.width = '100%';
  fader.style.height = '100%';
  fader.style.left = '0px';
  fader.style.top = '0px';
  fader.style.cursor = 'crosshair';
  fader.style.zIndex = '2147483647';

  return fader;
};

const getElementAfterEvent = ({
  mouseX,
  mouseY,
  faderElement,
  outlineElement,
  previousValue
}: ElementAfterEvent) => {
  faderElement.style.display = 'none';
  const target = getElementAtPosition(mouseX, mouseY);

  if (target === null) {
    return;
  }

  const { id, tagName } = target;
  faderElement.style.display = 'block';

  if (id === outlineElement.id || tagName === 'BODY' || tagName === 'HTML') {
    if (JSON.stringify(previousValue) !== 'null') {
      previousValue = null;
    }

    return;
  }

  const { height, width, left, top } = target.getBoundingClientRect();

  if (
    JSON.stringify(previousValue) !==
    JSON.stringify({ height, width, left, top })
  ) {
    previousValue = { height, width, left, top };

    updateOutlineElement(outlineElement, {
      height,
      width,
      left,
      top
    });
  }
};

export const selectElement = (callback, onCancel) => {
  let previousValue;
  let previousMouseValue = null;

  const outlineElement = createOutlineElement();
  const faderElement = createFaderElement();
  const instructionElement = createInstructionElement();

  window.BUGHERD_REACT_APP_SIDEBAR_MOUNT.insertAdjacentElement(
    'afterend',
    outlineElement
  );

  window.BUGHERD_REACT_APP_SIDEBAR_MOUNT.insertAdjacentElement(
    'afterend',
    faderElement
  );

  window.BUGHERD_REACT_APP_SIDEBAR_MOUNT.insertAdjacentElement(
    'afterend',
    instructionElement
  );

  const handleMouseMove = (event: any) => {
    const { clientX, clientY } = event;

    previousMouseValue = { x: clientX, y: clientY };

    getElementAfterEvent({
      mouseX: clientX,
      mouseY: clientY,
      faderElement,
      outlineElement,
      previousValue
    });
  };

  const handleScroll = () => {
    if (previousMouseValue) {
      const { x, y } = previousMouseValue;
      getElementAfterEvent({
        mouseX: x,
        mouseY: y,
        faderElement,
        outlineElement,
        previousValue
      });
    }
  };

  const handleCancel = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      document.removeEventListener('keyup', handleCancel);
      outlineElement.parentNode?.removeChild(outlineElement);
      faderElement.parentNode?.removeChild(faderElement);
      instructionElement.parentNode?.removeChild(instructionElement);
      onCancel(event);
    }
  };

  document.addEventListener('keyup', handleCancel);

  const handleClick = (event: any) => {
    event.preventDefault();
    event.stopPropagation();

    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('scroll', handleScroll);
    document.removeEventListener('click', handleClick);

    outlineElement.parentNode?.removeChild(outlineElement);
    faderElement.parentNode?.removeChild(faderElement);
    instructionElement.parentNode?.removeChild(instructionElement);

    const { clientX, clientY } = event;
    const mouseoverTarget = getElementAtPosition(clientX, clientY);

    requestAnimationFrame(() => {
      callback(mouseoverTarget, clientX, clientY);
    });
  };

  document.addEventListener('click', handleClick);
  document.addEventListener('mousemove', handleMouseMove);
  document.addEventListener('scroll', handleScroll);
};
